import React, { useState, useRef, forwardRef, useImperativeHandle, useCallback, useEffect } from 'react';
// Native MUI Components
import { 
  Box,
  Grid
} from '@mui/material';
import { SkiPassAgeLevels } from '@/types';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';
import AgeCategoryCard from './AgeCategoryCard';

interface SkiPassAgeLevelsPageProps {
  ageLevels: SkiPassAgeLevels;
  onUpdateAgeLevels: (ageLevels: SkiPassAgeLevels) => void;
}

export interface SkiPassAgeLevelsPageRef {
  save: () => void;
}

const SkiPassAgeLevelsPage = forwardRef<SkiPassAgeLevelsPageRef, SkiPassAgeLevelsPageProps>(({
  ageLevels,
  onUpdateAgeLevels
}, ref) => {
  const [formData, setFormData] = useState<SkiPassAgeLevels>(ageLevels);
  const { toast } = useToast();
  const { t } = useI18n();
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const handleSubmit = () => {
    onUpdateAgeLevels(formData);
    toast({
      title: t('common.save'),
      description: t('skipass.ageLevels.updateSuccess'),
    });
  };

  // Debounced save function
  const debouncedSave = useCallback((data: SkiPassAgeLevels) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set a new timeout for saving
    saveTimeoutRef.current = setTimeout(() => {
      onUpdateAgeLevels(data);
      toast({
        title: t('common.save'),
        description: t('skipass.ageLevels.updateSuccess'),
      });
    }, 1000); // 1 second delay
  }, [onUpdateAgeLevels, toast, t]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    save: handleSubmit
  }));

  const updateAgeLevel = (
    category: keyof SkiPassAgeLevels,
    field: string,
    value: string | number
  ) => {
    const updatedFormData = {
      ...formData,
      [category]: {
        ...formData[category],
        [field]: value
      }
    };
    setFormData(updatedFormData);
    
    // Debounced auto-save on change
    debouncedSave(updatedFormData);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={3} sx={{ pt: 2 }}>
        <AgeCategoryCard
          title="Child Category"
          emoji="👶"
          label={formData.child.label}
          min={formData.child.min}
          max={formData.child.max}
          onLabelChange={(value) => updateAgeLevel('child', 'label', value)}
          onMinChange={(value) => updateAgeLevel('child', 'min', value)}
          onMaxChange={(value) => updateAgeLevel('child', 'max', value)}
        />

        <AgeCategoryCard
          title="Youth Category"
          emoji="🧒"
          label={formData.youth.label}
          min={formData.youth.min}
          max={formData.youth.max}
          onLabelChange={(value) => updateAgeLevel('youth', 'label', value)}
          onMinChange={(value) => updateAgeLevel('youth', 'min', value)}
          onMaxChange={(value) => updateAgeLevel('youth', 'max', value)}
        />

        <AgeCategoryCard
          title="Adult Category"
          emoji="👨"
          label={formData.adult.label}
          min={formData.adult.min}
          max={formData.adult.max}
          onLabelChange={(value) => updateAgeLevel('adult', 'label', value)}
          onMinChange={(value) => updateAgeLevel('adult', 'min', value)}
          onMaxChange={(value) => updateAgeLevel('adult', 'max', value)}
        />

        <AgeCategoryCard
          title="Senior Category"
          emoji="👴"
          label={formData.senior.label}
          min={formData.senior.min}
          onLabelChange={(value) => updateAgeLevel('senior', 'label', value)}
          onMinChange={(value) => updateAgeLevel('senior', 'min', value)}
          showMaxField={false}
        />
      </Grid>
    </Box>
  );
});

SkiPassAgeLevelsPage.displayName = 'SkiPassAgeLevelsPage';

export default SkiPassAgeLevelsPage;
